import React, { Component } from 'react'
import Modal from "react-modal";
import Cookies from "universal-cookie";
import Stack from '@mui/material/Stack';
import { Accordion, AccordionDetails, AccordionSummary, MenuItem, TextField } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { renderButton, renderCancelButton } from "@/shared/FormUtils";
import RequiredAsterisk from "@/shared/forms/RequiredAsterisk";

const ITEM_INFO_FIELDS = [
  {name: "show-owner", label: "Owner", value: true},
  {name: "show-category", label: "Category", value: true},
  {name: "show-product-information", label: "Item Description", value: false},
  {name: "show-rental-price", label: "Rental Price", value: false},
  {name: "show-updated", label: "Last Updated by", value: false},
]

const ITEM_EVENT_INFO_FIELDS = [
  {name: "show-reserved", label: "Reserved", value: true},
  {name: "show-in-stock", label: "In Stock", value: true},
  {name: "show-shipped", label: "Qty. Shipped", value: true},
  {name: "show-distribution-location", label: "Distribution Location", value: true},
  {name: "show-pallet", label: "Pallet #", value: true},
  {name: "show-truck", label: "Truck #", value: true},
  {name: "show-notes", label: "Hold Notes", value: true},
  {name: "show-checked-in", label: "Checked In", value: true},
  {name: "show-collected", label: "Collected", value: true},
  {name: "show-onsite-notes", label: "Onsite Notes", value: true},
  {name: "show-return-notes", label: "Return Notes", value: true},
  {name: "show-returned", label: "Returned", value: true},
  {name: "show-damaged", label: "Damaged", value: true},
  {name: "show-lost-consumed", label: "Lost/Consumed", value: true},
  {name: "show-added-on-site", label: "Added On-Site", value: true},
]

const STATUS_FILTERS = [
  {name: "filter-status-reserved", label: "Reserved", value: true},
  {name: "filter-status-in-progress", label: "In Progress (Locked)", value: true},
  {name: "filter-status-packed", label: "Packed", value: true},
  {name: "filter-status-shipped", label: "Shipped", value: true},
  {name: "filter-status-checked-in", label: "Checked-in", value: true},
  {name: "filter-status-finalized", label: "Finalized", value: true},
]

const SORT_OPTIONS = [
  {label: "Item Name", value: "name"},
  {label: "Category", value: "category"},
  {label: "Status", value: "status"},
  {label: "Recipient", value: "recipient"},
  {label: "Shipping Location", value: "shipping-location"},
  {label: "Distribution Location", value: "distribution-location"},
]

const FLAG_FILTERS = [
  {name: "filter-flag-none", label: "No flags/tags", value: true},
  {name: "filter-flag-direct-to-site", label: "Direct to Site", value: true},
  {name: "filter-flag-dispose", label: "Dispose", value: true},
  {name: "filter-flag-not-returning", label: "Not Returning", value: true},
  {name: "filter-flag-ordered", label: "Ordered", value: true},
  {name: "filter-flag-temporary", label: "Temporary", value: true},
  {name: "filter-flag-not-finalized", label: "Not Finalized", value: true},
  {name: "filter-flag-deleted", label: "Deleted Item", value: true},
  {name: "filter-flag-insufficient", label: "Insufficient", value: true},
]

const cloneOptions = (options) => options.map((opt) => ({...opt}))

class GenerateEventReportModal extends Component {
  constructor(props) {
    super(props);
    this.state = this.initialState()

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.updateOption = this.updateOption.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  initialState() {
    return {
      modalOpen: false,
      name: "",
      nameError: false,
      sortBy: "name",
      itemInfoFields: cloneOptions(ITEM_INFO_FIELDS),
      itemEventInfoFields: cloneOptions(ITEM_EVENT_INFO_FIELDS),
      statusFilters: cloneOptions(STATUS_FILTERS),
      flagFilters: cloneOptions(FLAG_FILTERS),
    }
  }

  openModal(){
    this.setState({
      ...this.initialState(),
      modalOpen: true,
    })
  }

  closeModal(){
    this.setState({
      modalOpen: false
    })
  }

  renderAccessButton() {
    const { button } = this.props;
    if( button ) {
      return (
        <span onClick={this.openModal}>
          {button}
        </span>
      );
    }
    return (
      <a
        type="button"
        role="button"
        onClick={this.openModal}
      >
        Download Report
      </a>
    );
  }

  updateOption(groupKey, name, checked) {
    this.setState((prevState) => ({
      [groupKey]: prevState[groupKey].map((opt) => (
        opt.name === name ? {...opt, value: checked} : opt
      ))
    }))
  }

  renderCheckboxGroup(options, groupKey) {
    return (
      <div className="event-report-checkbox-grid">
        {options.map((opt) => (
          <label key={opt.name} className="event-report-selection">
            <input
              type="checkbox"
              name={opt.name}
              value={opt.name}
              checked={opt.value}
              onChange={(e) => this.updateOption(groupKey, opt.name, e.target.checked)}
            />
            {opt.label}
          </label>
        ))}
      </div>
    )
  }

  renderSection(title, options, groupKey) {
    return (
      <div className="event-report-section">
        <div className="report-menu-title">{title}</div>
        {this.renderCheckboxGroup(options, groupKey)}
      </div>
    )
  }

  renderFilterDrawer(title, options, groupKey) {
    return (
      <Accordion
        disableGutters
        elevation={0}
        className="event-report-filter-drawer"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div className="report-menu-title">{title}</div>
        </AccordionSummary>
        <AccordionDetails>
          {this.renderCheckboxGroup(options, groupKey)}
        </AccordionDetails>
      </Accordion>
    )
  }

  renderMenu(){
    const { name, nameError, sortBy, itemInfoFields, itemEventInfoFields, statusFilters, flagFilters } = this.state;
    const nameIsBlank = !name.trim()

    return (
      <div className="generate-event-report-menu">
        <div className="report-menu-title">Create Item Report</div>
        <div className="event-report-section">
          <label className="event-report-field-label">
            Report Name <RequiredAsterisk />
          </label>
          <TextField
            name="name"
            size="small"
            fullWidth
            required
            placeholder="Report Name"
            value={name}
            error={nameError}
            helperText={nameError ? "Report name is required" : ""}
            onChange={(e) => this.setState({name: e.target.value, nameError: false})}
          />
        </div>
        <div className="event-report-section">
          <label className="event-report-field-label">Sort By</label>
          <TextField
            select
            name="sortBy"
            size="small"
            fullWidth
            value={sortBy}
            onChange={(e) => this.setState({sortBy: e.target.value})}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        </div>

        {this.renderSection("Item Info", itemInfoFields, "itemInfoFields")}
        {this.renderSection("Item Event Info", itemEventInfoFields, "itemEventInfoFields")}
        <div className="event-report-section">
          {this.renderFilterDrawer("Item Status (Filter)", statusFilters, "statusFilters")}
          {this.renderFilterDrawer("Item Flags (Filter)", flagFilters, "flagFilters")}
        </div>

        <Stack direction="row" spacing={2} justifyContent="flex-end" className="mt-4">
          {renderButton("Create Report PDF", this.handleSubmit, {disabled: nameIsBlank})}
          {renderCancelButton("Cancel", this.closeModal)}
        </Stack>
      </div>
    )
  }

  collectOpts() {
    const { itemInfoFields, itemEventInfoFields, statusFilters, flagFilters } = this.state;
    const opts = {}
    ;[itemInfoFields, itemEventInfoFields, statusFilters, flagFilters].forEach((group) => {
      group.forEach((opt) => {
        opts[opt.name] = opt.value
      })
    })
    return opts
  }

  handleSubmit() {
    const { event, handleNewReport } = this.props;
    const name = this.state.name.trim()
    if (!name) {
      this.setState({ nameError: true })
      return
    }

    const cookies = new Cookies();
    const token = cookies.get("X-CSRF-Token");

    fetch(`/events/${event.slug}/report.pdf`, {
      method: "POST",
      redirect: "manual",
      body: JSON.stringify({
        sort_by: this.state.sortBy,
        opts: this.collectOpts(),
        name,
      }),
      headers: {
        "X-CSRF-Token": token,
        "Content-Type": 'application/json'
      }
    })
    .then((res) => res.json())
    .then((json) => {
      if (json.status == 400) {
        alert(`Error: ${json.message}`)
      } else {
        handleNewReport({
          completed: false,
          name,
        })
      }
      this.closeModal()
    })
  }

  render() {
    return(
      <React.Fragment>
        {this.renderAccessButton()}
        <Modal
          className="react-modal generate-report-modal"
          overlayClassName="react-modal-overlay"
          isOpen={this.state.modalOpen}
          onRequestClose={this.closeModal}
          contentLabel=""
        >
          {this.renderMenu()}
        </Modal>
      </React.Fragment>
    )
  }
}

export default GenerateEventReportModal
